import { PropsWithChildren, useCallback } from 'react';
import { Checkbox, CheckboxProps } from '../Checkbox/Checkbox';
import { Box } from '@ardoq/layout';

export interface Option extends PropsWithChildren<CheckboxProps> {
  name: string;
  options?: Option[];
}

interface GroupProps {
  option: Option;
  onChange: (name: string, isChecked: boolean) => void;
}

interface CheckboxTreeProps {
  options: Option[];
  onChange: (name: string, checked: boolean, options: Option[]) => void;
}

const Group = ({
  option: { name, isChecked, options, ...rest },
  onChange,
}: GroupProps) => {
  return (
    <Box marginBottom="small">
      <Checkbox
        key={name}
        name={name}
        isChecked={isChecked}
        onChange={() => onChange(name, !isChecked)}
        {...rest}
      />
      {options && (
        <Box marginY="small" marginLeft="xlarge">
          {options.map(option => (
            <Group key={option.name} option={option} onChange={onChange} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Updates the option recursively to reflect the new state of the tree,
 * taking into consideration inheritance of checked states and partially checked trees.
 */
function updateDeep(
  name: string,
  isChecked: boolean,
  option: Option,
  parents?: Option[]
) {
  const inheritChecked =
    (parents && parents.some(o => o.name === name)) || option.name === name
      ? isChecked
      : option.isChecked;

  if (!option.options) {
    return { ...option, isChecked: inheritChecked };
  }

  const updatedOptions: Option[] = option.options.map(o =>
    updateDeep(name, isChecked, o, parents ? [...parents, option] : [option])
  );
  const allChecked = updatedOptions.every(o => o.isChecked);
  const someChecked = updatedOptions.some(o => o.isChecked);

  return {
    ...option,
    isChecked: allChecked,
    isMixed: someChecked && !allChecked,
    options: updatedOptions,
  };
}

export const CheckboxTree = ({ options, onChange }: CheckboxTreeProps) => {
  const handleChange = useCallback(
    (name: string, isChecked: boolean) => {
      const updatedOptions = options.map(o => updateDeep(name, isChecked, o));
      onChange(name, isChecked, updatedOptions);
    },
    [options, onChange]
  );

  return (
    <div>
      {options.map(option => (
        <Group key={option.name} option={option} onChange={handleChange} />
      ))}
    </div>
  );
};
